import {
  pgTable,
  text,
  timestamp,
  uuid,
  boolean,
  jsonb,
  index,
} from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Status/type constants
// ---------------------------------------------------------------------------

export const ONBOARDING_STATUSES = ["url_submitted", "analyzing", "suggestions_ready", "configuring", "completed", "abandoned"] as const;
export type OnboardingStatus = (typeof ONBOARDING_STATUSES)[number];

// ---------------------------------------------------------------------------
// 1. Onboarding Sessions — URL-first AI-powered onboarding flow (F2)
// ---------------------------------------------------------------------------
// The AI analyzes the business URL, suggests configuration, and the user
// approves/modifies. Captures the full pipeline: crawl → suggest → accept.
// EU AI Act: suggestedConfig vs acceptedConfig tracks human override of AI.

export const onboardingSessions = pgTable("onboarding_sessions", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  analyzedUrl: text("analyzed_url").notNull(), // Business URL submitted by user
  status: text("status").notNull().default("url_submitted"), // OnboardingStatus
  // AI pipeline stages — each captures a snapshot for audit
  crawlResults: jsonb("crawl_results").$type<Record<string, unknown>>(), // What AI found on the website
  suggestedConfig: jsonb("suggested_config").$type<Record<string, unknown>>(), // What AI recommended
  acceptedConfig: jsonb("accepted_config").$type<Record<string, unknown>>(), // What user approved (may differ)
  // CQ6: crawlResults may contain publicly-available PII (emails, phones from website)
  piiClassification: jsonb("pii_classification"), // { containsEmail, containsPhone, source: "public_website" }
  detectedIndustry: text("detected_industry"),
  templateUsed: uuid("template_used"), // FK to industry_templates if a template was applied
  completedAt: timestamp("completed_at", tz),
  abandonedAt: timestamp("abandoned_at", tz),
  createdBy: uuid("created_by"),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_onboarding_tenant").on(table.tenantId),
  index("idx_onboarding_status").on(table.tenantId, table.status),
]);

// ---------------------------------------------------------------------------
// 2. Industry Templates — Pre-built configurations by industry
// ---------------------------------------------------------------------------
// PLATFORM-LEVEL: industry_templates has no tenantId. These are global seed
// data maintained by platform operations. Tenants select templates during
// onboarding but cannot create or modify them.
// CQ7: Queries against this table do NOT need tenant_id filtering.

export const industryTemplates = pgTable("industry_templates", {
  id: uuid("id").primaryKey().defaultRandom(),
  industryName: text("industry_name").notNull().unique(),
  displayName: text("display_name").notNull(),
  description: text("description"),
  templateConfig: jsonb("template_config").$type<Record<string, unknown>>().notNull().default({}),
  isDefault: boolean("is_default").notNull().default(false), // Selected when industry is "unknown"
  createdBy: text("created_by"), // Platform admin who created (CQ7)
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  // No tenantId index — platform-level table
  index("idx_industry_tpl_default").on(table.isDefault),
]);
